import { Link } from "react-router-dom";

import type { FaintProposal } from "../../../api/types";
import { Card, ConfidenceWord } from "../../../ui";
import { elementKindPhrase, withArticle } from "../../../ui/words";
import { whenWords } from "../lib/text";

/** "Rania Rowe proposed a component called Part 4, 21 Oct 2026, 09:15", or "You proposed ..." for one's own card. */
function proposalSentence(proposal: FaintProposal, mine: boolean): string {
  const who = mine ? "You" : (proposal.by?.display ?? "Someone");
  const since = proposal.since ? `, ${whenWords(proposal.since)}` : "";
  return `${who} proposed ${withArticle(elementKindPhrase(proposal.element_kind))} called ${proposal.name}${since}.`;
}

/**
 * The open proposals about this item that nobody has decided yet (UI-11, DEC-1016-96 R2-10), drawn faint as on the map:
 * who proposed what and since when, with a link to that session. A colleague's card is settled in their session, never
 * here; the page only shows it so two people do not do the same work.
 */
export function ProposedHereCard({ proposals, meId }: { proposals: readonly FaintProposal[]; meId: string | null | undefined }) {
  if (proposals.length === 0) return null;
  return (
    <Card aria-labelledby="proposed-title" className="proposed-here">
      <h2 id="proposed-title">Proposed here, not decided yet</h2>
      <ul className="proposed-here__list">
        {proposals.map((proposal) => {
          const mine = !!meId && proposal.by?.membership_id === meId;
          return (
            <li key={proposal.card_id} className="proposed-here__row" data-colleague={mine ? undefined : "true"}>
              <span>{proposalSentence(proposal, mine)}</span> <ConfidenceWord confidence={proposal.confidence} />{" "}
              {proposal.session_id && (
                <Link to={`/sessions/${encodeURIComponent(proposal.session_id)}`} aria-label={mine ? `Open your session: ${proposal.name}` : `Open ${proposal.by?.display ?? "their"}'s session: ${proposal.name}`}>
                  {mine ? "Open your session" : "Open their session"}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      <p className="muted">A proposal is settled in its own session. If one covers what you want to add, wait for it or talk to the person.</p>
    </Card>
  );
}
